import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertCircle, ExternalLink, Loader, Sparkles, TriangleAlert } from 'lucide-react';
import type { ModpackConfig, ModpackConfigEnv } from '../../types/modpackConfig';
import type { FleetStatus } from '../../hooks/useFleetStatus';
import { liveFor } from '../../hooks/useFleetStatus';
import { SectionHeader } from '../ui/SectionHeader';
import { LiveStatusBadge, describeLive } from './LiveStatus';
import { statusMonitorPathFor } from './statusPaths';
import { cn } from '../../utils/cn';
import type { MaintenanceMode, MaintenanceOptions } from '../../services/maintenanceApi';
import { describeMaintenanceChange, parseReleaseInput, releaseInputValue, sharedReleaseAt } from '../../utils/comingSoon';
import { ComingSoonBanner, WindowModeFields } from '../status/ComingSoon';

interface LiveStatusPanelProps {
  config: ModpackConfig;
  /** The cluster, this server included. */
  members: ModpackConfig[];
  env: ModpackConfigEnv;
  fleet: FleetStatus;
  /** Whether the viewer holds argus.maintenance. */
  canMaintain: boolean;
}

const when = (at: number | null) =>
  at ? new Date(at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

/**
 * What the network says about this server right now, and the one lever staff
 * have over it from here: maintenance.
 *
 * The readings are the status page's — the public server list for whether
 * players can reach it, Mr. Roboto's flag for whether it is down on purpose —
 * so the two pages never disagree. Maintenance is offered for this server or
 * the whole cluster, the same two choices the status page gives, because a
 * pack goes down for an update as a pack, not one region at a time.
 *
 * A window is plain maintenance or "coming soon", for a new server shown to
 * the public before it opens, with an optional release time. Starting either
 * on a server already in a window re-states it (how a release time moves), and
 * ending a coming-soon window opens the server to the public.
 */
/** What a start does to servers in the given state, in the words of the button. */
const startLabel = (mode: MaintenanceMode, alreadySoon: boolean) =>
  mode === 'coming_soon'
    ? (alreadySoon ? 'Update coming soon' : 'Mark coming soon')
    : (alreadySoon ? 'Switch to maintenance' : 'Start maintenance');

export const LiveStatusPanel = ({ config, members, env, fleet, canMaintain }: LiveStatusPanelProps) => {
  const live = liveFor(fleet, config.server_address);
  const description = describeLive(live);
  const clusterMembers = members.length > 0 ? members : [config];
  const clusterLookups = clusterMembers.map((m) => ({ config: m, live: liveFor(fleet, m.server_address) }));
  const inMaintenance = clusterLookups.filter((m) => m.live.maintenance).length;
  const allInMaintenance = inMaintenance === clusterLookups.length;
  const soonLookups = clusterLookups.filter((m) => m.live.comingSoon);
  const comingSoon = soonLookups.length;
  const plainMaintenance = inMaintenance - comingSoon;
  const allSoon = comingSoon === clusterLookups.length;

  const [busy, setBusy] = useState<'server' | 'cluster' | 'server-end' | 'cluster-end' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which kind of window a start makes. It opens on coming soon when this
  // server already is, with its release time filled in, so moving the date is
  // one edit.
  const [mode, setMode] = useState<MaintenanceMode>(() => (live.comingSoon ? 'coming_soon' : 'maintenance'));
  const [release, setRelease] = useState(() => releaseInputValue(live.releaseAt));
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const run = async (scope: NonNullable<typeof busy>, addresses: string[], next: boolean) => {
    if (busy) return;
    let options: MaintenanceOptions = {};
    if (next) {
      options = { mode };
      if (mode === 'coming_soon') {
        const parsed = parseReleaseInput(release, Date.now());
        if (!parsed.ok) {
          setReleaseError(parsed.error);
          return;
        }
        options.releaseAt = parsed.releaseAt;
      }
    }
    const wasComingSoon = !next && addresses.some((address) => liveFor(fleet, address).comingSoon);
    setBusy(scope);
    setError(null);
    setNote(null);
    setReleaseError(null);
    try {
      const result = await fleet.applyMaintenance(addresses, next, options);
      setNote(describeMaintenanceChange(result, { maintenance: next, ...options }, wasComingSoon));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That change could not be applied.');
    } finally {
      setBusy(null);
    }
  };

  const clusterName = config.cluster_name && config.cluster_name !== 'default' ? config.cluster_name : null;

  return (
    <div className="subcard p-4 space-y-4">
      <SectionHeader
        title="Live status"
        icon={Activity}
        description="From the public server list and Mr. Roboto's maintenance flag — the same feeds the status page reads."
        actions={
          <div className="flex items-center gap-2">
            <Link to={statusMonitorPathFor({ address: config.server_address })} className="glass-button-sm">
              Status monitor<ExternalLink className="w-4 h-4" />
            </Link>
            <Link to={`/chunk-throttle/${encodeURIComponent(config.server_address)}`} className="glass-button-sm">
              Chunk throttle<ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <p className="text-xs text-glass-400">This server</p>
          <LiveStatusBadge live={live} withDetail className="mt-1 text-sm" />
        </div>
        <div>
          <p className="text-xs text-glass-400">Players</p>
          <p className="text-sm text-white tabular-nums mt-1">
            {live.listing ? `${live.listing.players} / ${live.listing.maxPlayers}` : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-glass-400">{live.listing?.online ? 'Online since' : 'Last online'}</p>
          <p className="text-sm text-white tabular-nums mt-1">{when(live.listing?.lastOnlineAt ?? null)}</p>
        </div>
        <div>
          <p className="text-xs text-glass-400">Minecraft</p>
          <p className="text-sm text-white mt-1">{live.listing?.minecraftVersion || '—'}</p>
        </div>
      </div>

      {description.state === 'unlisted' && live.loaded && (
        <p className="text-xs text-glass-400">
          The public server list has no entry for this address. That is expected for a hidden or brand-new
          pack; otherwise check the address matches what the server reports.
        </p>
      )}

      <div className="border-t border-glass-200/10 pt-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-white inline-flex items-center gap-2">
            {comingSoon > 0 && plainMaintenance === 0
              ? <Sparkles className="w-4 h-4 text-violet-300" />
              : <TriangleAlert className={cn('w-4 h-4', inMaintenance > 0 ? 'text-amber-300' : 'text-glass-400')} />}
            Maintenance
            <span className="text-xs font-normal text-glass-400">
              {!live.loaded
                ? 'checking…'
                : inMaintenance === 0
                  ? 'nothing in maintenance'
                  : allSoon
                    ? (clusterLookups.length === 1 ? 'this server is coming soon' : 'the whole cluster is coming soon')
                    : allInMaintenance && comingSoon === 0
                      ? (clusterLookups.length === 1 ? 'this server is in maintenance' : 'the whole cluster is in maintenance')
                      : [plainMaintenance > 0 && `${plainMaintenance} of ${clusterLookups.length} in maintenance`,
                         comingSoon > 0 && `${comingSoon} of ${clusterLookups.length} coming soon`].filter(Boolean).join(' · ')}
            </span>
          </p>
          {canMaintain && env === 'production' && (
            <div className="flex flex-wrap items-center gap-1.5">
              {/* Starting plain maintenance where it is already on would do nothing. */}
              {!(mode === 'maintenance' && live.maintenance && !live.comingSoon) && <button
                type="button"
                className="btn-xs glass-button-secondary"
                disabled={busy !== null}
                onClick={() => run('server', [config.server_address], true)}
                title={mode === 'coming_soon' ? 'Show this server as coming soon' : 'Take this server down for maintenance'}
              >
                {busy === 'server' ? <Loader className="w-3 h-3 animate-spin" /> : null}
                {startLabel(mode, live.comingSoon)} · this server
              </button>}
              {live.maintenance && (
                <button
                  type="button"
                  className="btn-xs glass-button-secondary"
                  disabled={busy !== null}
                  onClick={() => run('server-end', [config.server_address], false)}
                  title={live.comingSoon ? 'Open this server to the public' : 'End maintenance on this server'}
                >
                  {busy === 'server-end' ? <Loader className="w-3 h-3 animate-spin" /> : null}
                  {live.comingSoon ? 'Open to the public' : 'End maintenance'} · this server
                </button>
              )}
              {clusterLookups.length > 1 && !(mode === 'maintenance' && allInMaintenance && comingSoon === 0) && (
                <button
                  type="button"
                  className="btn-xs glass-button-secondary"
                  disabled={busy !== null}
                  onClick={() => run('cluster', clusterLookups.map((m) => m.config.server_address), true)}
                  title={mode === 'coming_soon' ? 'Show the whole cluster as coming soon' : 'Take the whole cluster down for maintenance'}
                >
                  {busy === 'cluster' ? <Loader className="w-3 h-3 animate-spin" /> : null}
                  {startLabel(mode, allSoon)} · whole cluster ({clusterLookups.length})
                </button>
              )}
              {clusterLookups.length > 1 && inMaintenance > 0 && (
                <button
                  type="button"
                  className="btn-xs glass-button-secondary"
                  disabled={busy !== null}
                  onClick={() => run('cluster-end', clusterLookups.filter((m) => m.live.maintenance).map((m) => m.config.server_address), false)}
                  title={plainMaintenance === 0 ? 'Open every coming-soon server in the cluster to the public' : 'End the window on every server in the cluster'}
                >
                  {busy === 'cluster-end' ? <Loader className="w-3 h-3 animate-spin" /> : null}
                  {plainMaintenance === 0 ? 'Open to the public' : 'End maintenance'} · whole cluster ({inMaintenance})
                </button>
              )}
            </div>
          )}
        </div>

        {canMaintain && env === 'production' && (
          <WindowModeFields
            className="max-w-sm"
            mode={mode}
            onMode={(next) => { setMode(next); setReleaseError(null); }}
            release={release}
            onRelease={(value) => { setRelease(value); setReleaseError(null); }}
            error={releaseError}
            disabled={busy !== null}
          />
        )}

        {comingSoon > 0 && (
          <ComingSoonBanner
            className="rounded-lg"
            releaseAt={sharedReleaseAt(soonLookups.map((m) => ({ releaseAt: m.live.releaseAt })))}
            scope={allSoon ? undefined : `${comingSoon} of ${clusterLookups.length} servers`}
          />
        )}

        {clusterLookups.length > 1 && (
          <ul className="flex flex-wrap gap-x-4 gap-y-1">
            {clusterLookups.map(({ config: m, live: l }) => (
              <li key={m.id} className="inline-flex items-center gap-2 text-xs">
                <span className="font-mono text-glass-300">{m.region_name || m.server_address}</span>
                <LiveStatusBadge live={l} />
              </li>
            ))}
          </ul>
        )}

        {!canMaintain && (
          <p className="text-xs text-glass-500">
            Starting or ending a window needs <code className="font-mono">argus.maintenance</code>. Anyone can see one.
          </p>
        )}
        {canMaintain && env === 'development' && (
          <p className="text-xs text-glass-500">Maintenance applies to live servers; switch to production to change it.</p>
        )}
        {note && <p className="text-xs text-glass-300" role="status">{note}</p>}
        {error && (
          <p className="text-xs text-red-300 inline-flex items-center gap-1.5" role="alert">
            <AlertCircle className="w-3.5 h-3.5" />{error}
          </p>
        )}
      </div>

      {clusterName && (
        <p className="text-xs text-glass-500">
          <Link to={statusMonitorPathFor({ cluster: clusterName })} className="link">
            See the whole {clusterName} cluster on the status page
          </Link>
          {' '}— TPS, backups, hosts and heartbeats.
        </p>
      )}
    </div>
  );
};
